import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  DocumentArrowUpIcon,
  TrashIcon,
  AcademicCapIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  XMarkIcon,
  ArrowPathIcon,
  PlusIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import api from '../../services/api';

interface CertificateTemplate {
  id: number;
  category: string;
  template_path: string;
  uploaded_by: number;
  uploaded_by_name: string;
  uploaded_by_email: string;
  uploaded_at: string;
  name: string;
  description: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  thumbnail_url: string;
}

interface FormData {
  name: string;
  description: string;
  is_active: boolean;
}

const CertificateTemplates: React.FC = () => {
  const [templates, setTemplates] = useState<CertificateTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categories, setCategories] = useState<string[]>([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<CertificateTemplate | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { register, handleSubmit, formState: { errors } } = useForm<FormData>();

  useEffect(() => {
    fetchTemplates();
    fetchCategories();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/certificates/templates');
      setTemplates(response.data.templates);
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/videos');
      const videos = response.data.videos;
      const uniqueCategories = [...new Set(videos.map((v: any) => v.category).filter(Boolean))] as string[];
      setCategories(uniqueCategories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      if (file.type !== 'application/pdf') {
        setUploadError('Please upload a PDF file');
        setSelectedFile(null);
        return;
      }
      if (file.size > 10 * 1024 * 1024) { // 10MB
        setUploadError('File size must be less than 10MB');
        setSelectedFile(null);
        return;
      }
      setUploadError('');
      setSelectedFile(file);
    }
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedFile || !selectedCategory) {
      setUploadError('Please select both a file and a category');
      return;
    }

    try {
      setIsUploading(true);
      setUploadError('');
      
      const formData = new FormData();
      formData.append('template', selectedFile);
      formData.append('category', selectedCategory);

      await api.post('/certificates/template', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Reset form
      setSelectedFile(null);
      setSelectedCategory('');
      setShowUploadModal(false);
      
      // Refresh templates
      fetchTemplates();
    } catch (error) {
      console.error('Error uploading template:', error);
      setUploadError('Failed to upload template. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (category: string) => {
    if (!window.confirm(`ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການລຶບຮູບແບບນີ້? ການກະທຳນີ້ບໍ່ສາມາດຍົກເລີກໄດ້.`)) {
      return;
    }

    try {
      setIsDeleting(true);
      await api.delete(`/certificates/template/${encodeURIComponent(category)}`);
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      alert('Failed to delete template');
    } finally {
      setIsDeleting(false);
    }
  };

  const openUploadModal = () => {
    setSelectedFile(null);
    setSelectedCategory('');
    setUploadError('');
    setShowUploadModal(true);
  };

  const closeUploadModal = () => {
    setShowUploadModal(false);
    setSelectedFile(null);
    setSelectedCategory('');
    setUploadError('');
  };

  const handleAddTemplate = (template: CertificateTemplate) => {
    setEditingTemplate(template);
    setShowAddModal(true);
  };

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    try {
      if (editingTemplate) {
        await api.put(`/certificates/template/${editingTemplate.id}`, data);
      } else {
        await api.post('/certificates/template', data);
      }
      setShowAddModal(false);
      fetchTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteTemplate = (id: number) => {
    const template = templates.find(t => t.id === id);
    if (template) {
      handleDelete(template.category);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!editingTemplate) return;
    setIsDeleting(true);
    try {
      await api.delete(`/certificates/template/${editingTemplate.id}`);
      setShowDeleteModal(false);
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-0">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">ຮູບແບບໃບຢັ້ງຢືນ</h1>
        <p className="mt-2 text-gray-600">ຈັດການຮູບແບບໃບຢັ້ງຢືນສຳລັບນັກຮຽນ</p>
      </div>

      {/* Add Template Button */}
      <div className="mb-6">
            <button
          onClick={() => setShowAddModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
          <PlusIcon className="h-5 w-5 mr-2" />
          ເພີ່ມຮູບແບບໃໝ່
            </button>
          </div>
          
      {/* Templates Grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {templates.map((template) => (
          <div key={template.id} className="bg-white shadow rounded-lg overflow-hidden">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">{template.name}</h3>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleAddTemplate(template)}
                    className="text-indigo-600 hover:text-indigo-900"
                    title="ແກ້ໄຂຮູບແບບ"
                  >
                    <PencilIcon className="h-5 w-5" />
                  </button>
                    <button
                    onClick={() => handleDeleteTemplate(template.id)}
                    className="text-red-600 hover:text-red-900"
                    title="ລຶບຮູບແບບ"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              <div className="aspect-w-16 aspect-h-9 mb-4">
                <img
                  src={template.thumbnail_url}
                  alt={template.name}
                  className="object-cover rounded-lg"
                />
            </div>
              <div className="space-y-2">
                <p className="text-sm text-gray-500">
                  <span className="font-medium">ສະຖານະ:</span>{' '}
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    template.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {template.is_active ? 'ໃຊ້ງານຢູ່' : 'ບໍ່ໄດ້ໃຊ້ງານ'}
                  </span>
                </p>
                <p className="text-sm text-gray-500">
                  <span className="font-medium">ສ້າງເມື່ອ:</span>{' '}
                  {new Date(template.created_at).toLocaleDateString()}
                </p>
                <p className="text-sm text-gray-500">
                  <span className="font-medium">ອັບເດດລ່າສຸດ:</span>{' '}
                  {new Date(template.updated_at).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
        ))}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 overflow-y-auto z-50">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="absolute top-0 right-0 pt-4 pr-4">
                <button
                  type="button"
                  onClick={closeUploadModal}
                  className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                  title="Close modal"
                  aria-label="Close modal"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                      Upload Certificate Template
                    </h3>
                    
                    <form onSubmit={handleUpload} className="space-y-4">
                      <div>
                        <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                          Category
                        </label>
                        <select
                          id="category"
                          value={selectedCategory}
                          onChange={(e) => setSelectedCategory(e.target.value)}
                          className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                          required
                        >
                          <option value="">Select a category</option>
                          {categories.map((category) => (
                            <option key={category} value={category}>
                              {category}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label htmlFor="template-file" className="block text-sm font-medium text-gray-700">
                          PDF Template File
                        </label>
                        <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                          <div className="space-y-1 text-center">
                            <DocumentArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                            <div className="flex text-sm text-gray-600">
                              <label
                                htmlFor="template-file"
                                className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500"
                              >
                                <span>Upload a PDF template</span>
                                <input
                                  id="template-file"
                                  name="template-file"
                                  type="file"
                                  accept=".pdf"
                                  onChange={handleFileChange}
                                  className="sr-only"
                                  required
                                />
                              </label>
                              <p className="pl-1">or drag and drop</p>
                            </div>
                            <p className="text-xs text-gray-500">PDF up to 10MB</p>
                            {selectedFile && (
                              <p className="text-sm text-gray-900 mt-2">{selectedFile.name}</p>
                            )}
                          </div>
                        </div>
                      </div>

                      {uploadError && (
                        <div className="rounded-md bg-red-50 p-4">
                          <div className="flex">
                            <div className="flex-shrink-0">
                              <XMarkIcon className="h-5 w-5 text-red-400" />
                            </div>
                            <div className="ml-3">
                              <p className="text-sm text-red-700">{uploadError}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                        <button
                          type="submit"
                          disabled={isUploading || !selectedFile || !selectedCategory}
                          className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isUploading ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Uploading...
                            </>
                          ) : (
                            <>
                              <DocumentArrowUpIcon className="h-5 w-5 mr-2" />
                              Upload Template
                            </>
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={closeUploadModal}
                          className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Template Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                {editingTemplate ? 'ແກ້ໄຂຮູບແບບ' : 'ເພີ່ມຮູບແບບໃໝ່'}
              </h3>
            </div>
            <form onSubmit={handleSubmit(onSubmit)} className="p-6">
              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    ຊື່ຮູບແບບ
                  </label>
                  <input
                    type="text"
                    id="name"
                    {...register('name', { required: true })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">ກະລຸນາປ້ອນຊື່ຮູບແບບ</p>
                  )}
                </div>

                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                    ຄຳອະທິບາຍ
                  </label>
                  <textarea
                    id="description"
                    {...register('description')}
                    rows={3}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="template_file" className="block text-sm font-medium text-gray-700">
                    ໄຟລ໌ຮູບແບບ
                  </label>
                  <input
                    type="file"
                    id="template_file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                    className="mt-1 block w-full"
                  />
                </div>

                <div>
                  <label htmlFor="thumbnail" className="block text-sm font-medium text-gray-700">
                    ຮູບພາບຕົວຢ່າງ
                  </label>
                  <input
                    type="file"
                    id="thumbnail"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="mt-1 block w-full"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active"
                    {...register('is_active')}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                    ເປີດໃຊ້ງານຮູບແບບນີ້
                  </label>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  ຍົກເລີກ
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <ArrowPathIcon className="animate-spin -ml-1 mr-2 h-4 w-4" />
                      ກຳລັງບັນທຶກ...
                    </>
                  ) : (
                    'ບັນທຶກ'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">ຢືນຢັນການລຶບ</h3>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-500">
                ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການລຶບຮູບແບບນີ້? ການກະທຳນີ້ບໍ່ສາມາດຍົກເລີກໄດ້.
              </p>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowDeleteModal(false)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  ຍົກເລີກ
                </button>
                <button
                  type="button"
                  onClick={handleDeleteConfirm}
                  disabled={isDeleting}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                >
                  {isDeleting ? (
                    <>
                      <ArrowPathIcon className="animate-spin -ml-1 mr-2 h-4 w-4" />
                      ກຳລັງລຶບ...
                    </>
                  ) : (
                    'ລຶບ'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CertificateTemplates;